/**
 * Bot nickname & profile generation (spec §11 "Bots — 1000 profiles").
 *
 * Nicknames are built from components — prefix + root + suffix/number — which yields
 * well over the ~50k combinations the spec asks for, then filtered against a profanity
 * blacklist and de-duplicated. A match draws 25 profiles whose names never repeat
 * within the match; a per-match seed (mulberry32) plus the entry timestamp makes the
 * roster differ match-after-match.
 */

import { mulberry32 } from '../map/prng';
import { rosterName } from './roster';

export type SkillTier = 'rookie' | 'regular' | 'veteran' | 'elite';
export type Personality = 'aggressor' | 'farmer' | 'camper' | 'tactician' | 'coward';

export interface BotProfile {
  id: string;
  nickname: string;
  skillTier: SkillTier;
  personality: Personality;
  /** Hero appearance seed — the client resolves it to a paper-doll like players. */
  sprite: string;
}

// ~14 × ~26 × ~16 = ~5.8k base stems; with the numeric suffix branch the space is
// far past the 50k the spec wants. Kept readable/gamey, not random noise.
const PREFIXES = ['Dark', 'Shroom', 'Lava', 'Ash', 'Grim', 'Mush', 'Toxic', 'Ember', 'Night', 'Fungal', 'Crimson', 'Basalt', 'Venom', 'Cinder'];
const ROOTS = ['fang', 'shroom', 'blade', 'creep', 'wraith', 'stalker', 'reaper', 'bloom', 'husk', 'maw', 'thorn', 'gloom', 'rot', 'pyre', 'shade', 'grasp', 'brand', 'cap', 'spire', 'drift', 'scourge', 'bane', 'crawler', 'ember', 'grove', 'fen'];
const SUFFIXES = ['', '', 'x', 'zz', '_', 'er', 'ling', 'oid', '99', '00', '__', 'XD', 'TV', 'YT', 'ka', 'us'];

// Substrings that must never appear in a generated (or numbered) nickname. Kept
// deliberately small and lowercase-compared; extend as needed.
const PROFANITY = ['fuck', 'shit', 'cunt', 'nigg', 'fag', 'rape', 'nazi', 'slut', 'whore', 'dick', 'cock', 'porn'];

/**
 * Substrings that are INNOCENT despite containing one of the above — the
 * Scunthorpe problem. Checked before the block list, so a real handle is not
 * silently deleted by a naive substring match.
 *
 * Kept deliberately tiny and explicit: this is an allow-list of known-good words,
 * never a pattern. Widening it is how a filter stops filtering. `manishito` is
 * Manish + the -ito suffix used across the rest of the roster, and it was the only
 * one of 601 supplied names the block list ate. `rameshito` is the same shape
 * (Ramesh + -ito), the only one of the owner's 3,869 names it ate.
 */
const ALLOWED = ['manishito', 'rameshito'];

/** True if `name` contains a blacklisted substring (case-insensitive). */
export function isClean(name: string): boolean {
  const lower = name.toLowerCase();
  if (ALLOWED.some((w) => lower === w)) return true;
  const l = name.toLowerCase().replace(/[^a-z]/g, '');
  return !PROFANITY.some((bad) => l.includes(bad));
}

const pick = <T>(rng: () => number, arr: T[]): T => arr[Math.floor(rng() * arr.length)];

/** One nickname from the component grammar (may still collide — caller de-dupes). */
function makeNickname(rng: () => number): string {
  const base = pick(rng, PREFIXES) + pick(rng, ROOTS) + pick(rng, SUFFIXES);
  // ~40% get a trailing number, widening the space and reading like real handles.
  return rng() < 0.4 ? base + (1 + Math.floor(rng() * 998)) : base;
}

/** Consecutive roster collisions before falling back to the generated grammar. */
const ROSTER_MISS_LIMIT = 40;

const TIER_TABLE: [SkillTier, number][] = [['rookie', 0.20], ['regular', 0.45], ['veteran', 0.25], ['elite', 0.10]];
const PERSONALITIES: Personality[] = ['aggressor', 'farmer', 'camper', 'tactician', 'coward'];

/** Draw a skill tier from the spec's 20/45/25/10 distribution. */
export function rollTier(rng: () => number): SkillTier {
  let x = rng();
  for (const [tier, p] of TIER_TABLE) { if (x < p) return tier; x -= p; }
  return 'regular';
}

/**
 * Build a match roster of `count` unique-nicknamed bot profiles from a seed.
 * De-dupes nicknames within the roster and rejects profanity; the seed makes the
 * roster reproducible (for the sim harness) yet different per match.
 */
export function generateRoster(seed: number, count = 25): BotProfile[] {
  const rng = mulberry32(seed);
  const used = new Set<string>();
  const out: BotProfile[] = [];
  let guard = 0;
  let rosterMisses = 0;
  while (out.length < count && guard++ < count * 200) {
    // Prefer the CURATED roster, fall back to the grammar. The generator makes
    // fantasy handles ("Shroomblade99") which read as scenery — a whole lobby of
    // them announces itself as set dressing; the roster reads like handles people
    // actually pick.
    //
    // The switch is driven by consecutive MISSES, not by "is the roster
    // theoretically exhausted". Gating on `used.size < ARENA_ROSTER.length` stalled:
    // with one name left unused, the draw kept landing on taken ones and burned the
    // whole guard, so a 1000-profile pool came back with 600.
    const fromRoster = rosterMisses < ROSTER_MISS_LIMIT;
    const nickname = fromRoster ? rosterName(seed, out.length + guard) : makeNickname(rng);
    if (!isClean(nickname) || used.has(nickname)) { if (fromRoster) rosterMisses++; continue; }
    if (fromRoster) rosterMisses = 0;
    used.add(nickname);
    out.push({
      id: `bot_${seed}_${out.length}`,
      nickname,
      skillTier: rollTier(rng),
      personality: pick(rng, PERSONALITIES),
      sprite: `arena_${Math.floor(rng() * 1_000_000)}`,
    });
  }
  return out;
}

// ─── Seating: real players take bot slots ───────────────────────────────────
// The FAQ promises "the field fills straight away, and real players take those
// places as they join". The server's lobby (backend/src/game/arena/arena-lobby.ts)
// decides who sits in which slot; this turns that seating into the fighters the
// match renders.

/** One field slot as the server seated it. Mirrors backend `Seat`. */
export interface MatchSeat {
  slot: number;
  kind: 'human' | 'bot';
  userId?: string | null;
  username?: string | null;
}

export interface Fighter {
  /** Field slot — also the bot-name key, so it is stable across lobby sizes. */
  slot: number;
  id: string;
  nickname: string;
  /** A real account is behind this fighter (you or another player in the lobby). */
  isHuman: boolean;
  /** The fighter THIS client drives. Exactly one per match. */
  isPlayer: boolean;
  /** The bot profile for the slot; null when a human took it. */
  bot: BotProfile | null;
}

/** An all-bot field: what a solo player gets when the lobby is unreachable. */
export function soloSeats(youName: string, fieldSize = 25): MatchSeat[] {
  return Array.from({ length: fieldSize }, (_, slot) =>
    slot === 0
      ? { slot, kind: 'human' as const, userId: null, username: youName }
      : { slot, kind: 'bot' as const });
}

/**
 * Build the match's fighters from the seated field.
 *
 * The bot roster is generated at FULL field size and indexed BY SLOT — never by
 * "n-th bot". That is what keeps a match reproducible from its seed while the
 * number of humans varies: seating one more player turns one slot from bot to
 * human and leaves every other slot on the name, tier and personality it already
 * had. Indexing by bot ordinal would have renumbered the whole tail and the sim
 * harness (arena/sim.ts, which also generates FIELD-many profiles by slot) would
 * replay a different roster than the one that was fought.
 *
 * `youName` is only a fallback label: the local player is identified by `youId`
 * where the server gave one, and otherwise by the sole human seat matching the
 * name — a lobby the client could not reach still has to render.
 */
export function buildFighters(
  seed: number,
  seats: readonly MatchSeat[],
  youName: string,
  youId?: string | null,
): Fighter[] {
  const bots = generateRoster(seed, seats.length);
  let playerTaken = false;
  // Two fighters sharing an id is not a cosmetic problem: the match view looks
  // heroes and combat state up BY id (`heroes.find(x => x.id === ...)`), so a
  // duplicate resolves to the first one and the second fighter becomes literally
  // unhittable — damage in range that never lands (defect 17). The old fallback
  // `bots[slot] ?? bots[bots.length - 1]` produced exactly that whenever a seat's
  // slot ran past the roster. Hand out an unused profile instead.
  const takenBots = new Set<number>();
  const claimBot = (slot: number) => {
    let i = slot >= 0 && slot < bots.length && !takenBots.has(slot) ? slot : -1;
    if (i < 0) for (let j = 0; j < bots.length; j++) if (!takenBots.has(j)) { i = j; break; }
    if (i < 0) return null;                    // roster exhausted — caller falls back
    takenBots.add(i);
    return bots[i];
  };
  const fighters = seats.map((s, i) => {
    const slot = s.slot ?? i;
    if (s.kind !== 'human') {
      // Only BOT seats draw from the roster, so a lobby with more humans in it still
      // leaves every bot in the slot it would have had (see this function's contract).
      const bot = claimBot(slot) ?? { ...bots[bots.length - 1], id: `bot_slot_${slot}` };
      return { slot, id: bot.id, nickname: bot.nickname, isHuman: false, isPlayer: false, bot };
    }
    const isPlayer = !playerTaken && (youId ? s.userId === youId : s.username === youName || s.userId == null);
    if (isPlayer) playerTaken = true;
    return {
      slot,
      id: isPlayer ? 'player' : `human_${slot}`,
      nickname: (isPlayer ? youName : s.username) || `Player ${slot + 1}`,
      isHuman: true, isPlayer, bot: null,
    };
  });
  // The match is unplayable without a fighter to drive, so if the seating somehow
  // contained no seat we could claim (a stale lobby, a renamed account), the first
  // human seat becomes ours rather than leaving the player a spectator.
  if (!fighters.some((f) => f.isPlayer)) {
    const mine = fighters.find((f) => f.isHuman);
    if (mine) { mine.isPlayer = true; mine.id = 'player'; mine.nickname = youName; }
  }
  return fighters;
}

/** The pool of 1000 profiles the matches draw from (spec: fixed pool of 1000). */
export function generatePool(seed = 1, size = 1000): BotProfile[] {
  return generateRoster(seed, size);
}
